import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSubscriptionStatus(null);
    setTrialExpired(false);
  }, []);

  // On mount: validate session by calling /auth/me
  useEffect(() => {
    const validateSession = async () => {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) { setLoading(false); return; }
      try {
        const res = await api.get('/auth/me');
        const userData = res.data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        // Check subscription
        try {
          const subRes = await api.get('/subscription/status');
          setSubscriptionStatus(subRes.data);
          setTrialExpired(subRes.data?.trial_expired === true);
        } catch { /* subscription check non-critical */ }
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };
    validateSession();
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: userData } = res.data;
    // Store in localStorage as fallback (cookie is set by backend)
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Check subscription after login
    try {
      const subRes = await api.get('/subscription/status');
      setSubscriptionStatus(subRes.data);
      setTrialExpired(subRes.data?.trial_expired === true);
    } catch { /* subscription check non-critical */ }
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); }
    catch { /* logout API non-critical */ }
    clearSession();
  }, [clearSession]);

  const signup = useCallback(async (data) => {
    const res = await api.post('/auth/signup', data);
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return res.data;
  }, []);

  // Refresh subscription status
  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const subRes = await api.get('/subscription/status');
      setSubscriptionStatus(subRes.data);
      setTrialExpired(subRes.data?.trial_expired === true);
    } catch { /* subscription refresh non-critical */ }
  }, [user]);

  useEffect(() => {
    if (user) refreshSubscription();
  }, [user, refreshSubscription]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, signup,
      subscriptionStatus, trialExpired, refreshSubscription, setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
